import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { pairScreen } from '../services/screens'
import { isIOS, isStandalone } from '../utils/install'
import { ChefHatIcon } from '../components/icons'
import { ConfirmDialog } from '../components/pos/ConfirmDialog'
import './PairScreenPage.css'

/**
 * adissoo.com/ekran: tableti mutfak ekranı olarak bağlama (kullanıcı, 11 Eki
 * 2026). Üç yol: karekod (bağlantı ?kod= ile gelir), adresi açıp kodu yazmak,
 * paylaşılan bağlantı.
 *
 * iPad'de sıra önemli: ana ekrana eklenen uygulama Safari'deki oturumu
 * görmüyor. Safari'de açılınca önce yönerge çıkıyor: ana ekrana ekle, oradan
 * aç, kodu orada yaz.
 */
export function PairScreenPage() {
  const [params] = useSearchParams()
  const fromLink = (params.get('kod') ?? '').replace(/\D/g, '').slice(0, 6)
  const { switchTenant, login, logout, admin, roleLabels, loading, isAuthenticated, isKitchen, isKiosk, tenantId } = useAuth()
  const navigate = useNavigate()

  const needsGuide = isIOS() && !isStandalone()
  const [skipGuide, setSkipGuide] = useState(false)
  const [code, setCode] = useState(fromLink)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const tried = useRef('')
  /**
   * Cihazda başka biri (garson, kasiyer, patron) girişliyken gelen kod: önce
   * sorulur, bağlanınca o kişinin oturumu kapanır (kullanıcı, 1 Kas 2026).
   */
  const [ask, setAsk] = useState<string | null>(null)
  const someoneIn = isAuthenticated && !isKitchen && !isKiosk

  // Mutfak ekranı ya da kiosk oturumu gelince ekrana. Bağlanırken de buradan:
  // girişin hemen ardından gidilince oturum henüz eski kişiydi (garson), kiosk
  // koruması onu ana sayfaya, garson ekranına atıyordu.
  useEffect(() => {
    if (!isAuthenticated || !tenantId) return
    if (isKitchen) navigate(`/${tenantId}/mutfak`, { replace: true })
    else if (isKiosk) navigate(`/${tenantId}/kiosk`, { replace: true })
  }, [isAuthenticated, isKitchen, isKiosk, tenantId, navigate])

  // Oturum gelmezse (üyelik bulunamadı vb.) "Bağlanıyor…"da kalmasın.
  useEffect(() => {
    if (!busy) return
    const t = window.setTimeout(() => {
      setError('Bağlanamadı. Patrondan yeni kod isteyin.')
      setBusy(false)
      tried.current = ''
    }, 20_000)
    return () => window.clearTimeout(t)
  }, [busy])

  const pair = async (c: string, confirmed = false) => {
    if (c.length !== 6 || busy || tried.current === c) return
    if (someoneIn && !confirmed) {
      setAsk(c)
      return
    }
    tried.current = c
    setBusy(true)
    setError(null)
    try {
      const r = await pairScreen(c)
      if (someoneIn) await logout()
      switchTenant(r.tenantId)
      await login(r.email, r.password, r.tenantId)
    } catch (e) {
      setError((e as Error).message || 'Bağlanamadı. İnternet bağlantınızı kontrol edin.')
      setBusy(false)
      tried.current = ''
    }
  }

  const who = admin ? `${roleLabels[admin.role]} ${admin.displayName || admin.username}`.trim() : ''
  const askDialog = ask && (
    <ConfirmDialog
      title="Bu cihazda giriş yapılmış"
      body={
        <>
          Bu cihazda <b style={{ color: 'var(--foreground)' }}>{who}</b> olarak giriş yapılmış. Ekran olarak bağlanınca bu hesaptan çıkış
          yapılır; tekrar girmek için kullanıcı adı ve PIN gerekir.
        </>
      }
      confirmLabel="Çıkış yap ve bağla"
      closeX
      onConfirm={() => void pair(ask, true)}
      onClose={() => setAsk(null)}
    />
  )

  // Bağlantıyla gelen kod: yönerge gerekmiyorsa hemen bağlan.
  // Oturum okunmadan değil: cihazda kimin girişli olduğu bilinmeden sorulmadan
  // bağlanırdı.
  useEffect(() => {
    if (loading) return
    if (fromLink.length === 6 && (!needsGuide || skipGuide)) void pair(fromLink)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skipGuide, loading])

  const type = (d: string) => {
    if (busy) return
    setError(null)
    setCode((c) => {
      const next = (c + d).slice(0, 6)
      if (next.length === 6) void pair(next)
      return next
    })
  }
  const back = () => !busy && setCode((c) => c.slice(0, -1))

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (/^\d$/.test(e.key)) type(e.key)
      else if (e.key === 'Backspace') back()
      else if (e.key === 'Enter') void pair(code)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  if (needsGuide && !skipGuide) {
    return (
      <div className="pair">
        <div className="pair__card">
          <span className="pair__icon">
            <ChefHatIcon />
          </span>
          <h1>iPad'de önce ana ekrana ekleyin</h1>
          <p className="pair__lead">
            Mutfak ekranı ana ekrandan açılınca tam ekran çalışır. Sıra önemli: Safari'de bağlarsanız
            ana ekrandaki uygulama bağlantıyı görmez.
          </p>
          <ol className="pair__steps">
            <li>
              <b>1</b>
              <span>
                Safari'nin üst çubuğundaki <ShareGlyph /> <strong>Paylaş</strong> simgesine dokunun.
              </span>
            </li>
            <li>
              <b>2</b>
              <span>
                Listeden <strong>Ana Ekrana Ekle</strong>'yi seçin, sağ üstteki <strong>Ekle</strong>'ye
                dokunun.
              </span>
            </li>
            <li>
              <b>3</b>
              <span>
                Ana ekrandaki <strong>Adissoo</strong> simgesine dokunarak uygulamayı açın.
              </span>
            </li>
            <li>
              <b>4</b>
              <span>
                Giriş ekranının altındaki <strong>Mutfak ekranı bağla</strong>'ya dokunun ve kodu yazın
                {fromLink.length === 6 && (
                  <>
                    : <strong className="pair__inline-code">{fromLink.slice(0, 3)} {fromLink.slice(3)}</strong>
                  </>
                )}
                .
              </span>
            </li>
          </ol>
          <p className="pair__note">Kod 10 dakika geçerli. Süre dolarsa patrondan yeni kod isteyin.</p>
          <button type="button" className="pair__skip" onClick={() => setSkipGuide(true)}>
            Ana ekrana eklemeden Safari'de devam et
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="pair">
      <div className="pair__card">
        <span className="pair__icon">
          <ChefHatIcon />
        </span>
        <h1>Mutfak ekranını bağlayın</h1>
        <p className="pair__lead">
          Patronun Ayarlar &gt; Mutfak ekranları'nda gördüğü 6 haneli kodu yazın.
        </p>

        <div className={`pair__boxes ${error ? 'is-bad' : ''}`} aria-label="Bağlama kodu" role="group">
          {Array.from({ length: 6 }, (_, i) => (
            <span key={i} className={`${i === code.length && !busy ? 'is-cursor' : ''} ${i === 2 ? 'is-gap' : ''}`}>
              {code[i] ?? ''}
            </span>
          ))}
        </div>

        <p className={`pair__msg ${error ? 'is-bad' : ''}`} role="status">
          {busy ? 'Bağlanıyor…' : error ?? ' '}
        </p>

        <div className="pair__pad">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
            <button key={d} type="button" onClick={() => type(d)} disabled={busy}>
              {d}
            </button>
          ))}
          <button type="button" className="pair__pad-del" onClick={() => setCode('')} disabled={busy}>
            Sil
          </button>
          <button type="button" onClick={() => type('0')} disabled={busy}>
            0
          </button>
          <button type="button" className="pair__pad-back" onClick={back} disabled={busy} aria-label="Son rakamı sil">
            ⌫
          </button>
        </div>

        <a className="pair__login" href="/giris">
          Personel girişine dön
        </a>
      </div>
      {askDialog}
    </div>
  )
}

/** iOS paylaş simgesi (kutudan çıkan ok), yönergede metnin içinde. */
function ShareGlyph() {
  return (
    <svg className="pair__glyph" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3v12" />
      <path d="m8 7 4-4 4 4" />
      <path d="M8 11H6a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-8a1 1 0 0 0-1-1h-2" />
    </svg>
  )
}
